import React, { useEffect } from "react";
import { Layers, Zap, Globe, Plug, Shield, Settings } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import "./Kfc.scss";

const Kfc = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletsListLeft = (
    <BulletList>
      <BulletItem>Real-time stock availability</BulletItem>
      <BulletItem>Regional and local marketing campaigns</BulletItem>
      <BulletItem>Time of day and day of week</BulletItem>
      <BulletItem>Major sporting events and promotions</BulletItem>
      <BulletItem>Customer preferences and localisation</BulletItem>
    </BulletList>
  );

  const bulletsListLeftSecondTier = (
    <BulletList>
      <BulletItem>In-restaurant screens</BulletItem>
      <BulletItem>Self-service kiosks</BulletItem>
      <BulletItem>Mobile apps</BulletItem>
      <BulletItem>Third-party delivery platforms</BulletItem>
    </BulletList>
  );

  const bulletsListRight = (
    <BulletList>
      <BulletItem>Responds to real-time business signals</BulletItem>
      <BulletItem>
        Generates menus dynamically per location and channel
      </BulletItem>
      <BulletItem>Distributes updates globally within seconds</BulletItem>
      <BulletItem>
        Integrates seamlessly with internal CMS tools and external delivery
        partners
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Dynamic Real-Time Menu Generation"
        subtitle="Powering global menus across restaurants, kiosks, and delivery platforms — in real time, at planetary scale."
        category="ENGINEERING"
        role="Principal Software Engineer — Global Menu & Content Platforms"
        year="2025 - 2026"
        platform="Restaurants, Self-Service Kiosks, Web, Mobile, Third-Party Delivery Platforms"
        technologies={[
          "Node.js",
          " Java",
          " Microservices",
          " Event-Driven Architecture",
          " REST APIs",
          " Cloud Infrastructure",
          " Global CMS",
          " Real-Time Data Pipelines",
        ]}
        client="KFC Global / Yum"
      />
      <VisualShowcase
        imageUrl={images.kfc7}
        imageAlt="Kfc food options"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "One Brand. Thousands of Menus. Zero Room for Error.",
          "KFC operates at a scale few brands ever reach — thousands of restaurants across dozens of markets, each with its own operational realities.",
          "Menus aren’t static. They change constantly based on:",
        ]}
        bulletsLeft={bulletsListLeft}
        bulletsLeftSecondTier={bulletsListLeftSecondTier}
        bulletsRight={bulletsListRight}
        extraChallengeContent={[
          "The challenge wasn’t simply displaying menus — it was generating them dynamically, consistently, and reliably across:",
        ]}
        extraChallengeContentSecondTier={[
          "All while ensuring performance, accuracy, and uptime at a global scale.",
        ]}
        solutionContent={[
          "A Real-Time, Event-Driven Menu Platform at Global Scale",
          "I joined KFC’s global engineering team responsible for building and maintaining the backend systems that power dynamic menu generation worldwide.",
          "The solution was a distributed, microservice-based platform that:",
        ]}
        extraSolutionContent={[
          "This architecture enables KFC to evolve menus continuously — without redeployments, manual intervention, or downtime.",
        ]}
        metrics={[
          {
            value: "Global",
            label: "Menu platform serving thousands of restaurants",
          },
          { value: "Real-time", label: "Menu updates based on stock & events" },
          {
            value: "Multi-channel",
            label: "Restaurants, kiosks & delivery apps",
          },
          { value: "24/7", label: "High-availability global API uptime" },
          {
            value: "Seamless",
            label: "Integration with Uber Eats, Deliveroo & Just Eat",
          },
        ]}
      />
      <VisualShowcase
        title="Menus That Adapt in Real Time"
        description="A snapshot of how KFC’s menus dynamically adapt across kiosks and restaurant screens — responding instantly to operational and marketing changes."
        imageUrl={images.kfc5}
        imageAlt="Kiosks one"
        layout="full"
      />
      <VisualShowcase
        title="A Global Menu Ecosystem"
        description="From in-restaurant kiosks to delivery platforms, the same real-time menu system powers every customer touchpoint — ensuring consistency, accuracy, and speed across the globe."
        imageUrl={images.kfc9}
        imageAlt="Kiosks two"
        layout="full"
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
      />
      <KeyFeatures
        title="Dynamic Menu Platform Capabilities"
        description="Core system features designed for scale, flexibility, and real-time decision making."
        features={[
          {
            icon: Layers,
            title: "Dynamic menu generation",
            description:
              "Menus are generated on demand based on location, availability, campaigns, and contextual rules.",
          },
          {
            icon: Zap,
            title: "Real-time updates",
            description:
              "Stock changes, promotions, and events propagate globally within seconds.",
          },
          {
            icon: Globe,
            title: "Global distribution",
            description:
              "Menu data is reliably delivered to thousands of restaurants and kiosks worldwide.",
          },
          {
            icon: Plug,
            title: "Third-party delivery integration",
            description:
              "Seamless menu synchronisation with Uber Eats, Deliveroo, and Just Eat.",
          },
          {
            icon: Shield,
            title: "High availability & resilience",
            description:
              "Built to operate 24/7 with fault tolerance and graceful degradation.",
          },
          {
            icon: Settings,
            title: "Business-controlled configuration",
            description:
              "Menus evolve through CMS-driven rules without requiring code changes or redeployments.",
          },
        ]}
      />
      <ProcessSection
        title="Engineering a Real-Time Global Menu Platform"
        description="A systems-driven approach designed to balance global scale, local flexibility, and real-time operational change."
        phases={[
          {
            number: "01",
            title: "Domain Mapping & Operational Constraints",
            description:
              "Developed a deep understanding of KFC’s global menu domain, including operational realities, regional variation, and the business drivers behind real-time change.",
            deliverables: [
              "Menu domain modelling",
              "Operational constraint analysis",
              "Channel and platform mapping",
              "Stakeholder alignment across regions",
            ],
          },
          {
            number: "02",
            title: "Platform Architecture & Data Flow Design",
            description:
              "Designed and evolved a distributed, microservice-based architecture capable of generating and distributing menus dynamically across multiple channels.",
            deliverables: [
              "Microservice architecture definition",
              "Real-time data flow design",
              "CMS integration strategy",
              "Channel-specific menu generation rules",
            ],
          },
          {
            number: "03",
            title: "Implementation & Global Distribution",
            description:
              "Built and maintained backend services responsible for real-time menu generation and global dissemination to restaurants, kiosks, and delivery partners.",
            deliverables: [
              "Menu generation services",
              "Global API endpoints",
              "Integration with delivery platforms",
              "Performance and reliability improvements",
            ],
          },
          {
            number: "04",
            title: "Operational Reliability & Continuous Evolution",
            description:
              "Focused on operating the platform at scale, improving resilience, supporting new markets, and enabling continuous change without service disruption.",
            deliverables: [
              "Monitoring and alerting",
              "Operational playbooks",
              "Scalability and resilience enhancements",
              "Ongoing platform iteration",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactTitle="Real-time systems enabling global operational agility"
        impactLabel="Project Impact"
        impactDescription="This project enabled KFC to operate menus as a living system — responsive to real-world conditions rather than static configurations. By centralising menu logic while allowing local flexibility, the platform supports rapid experimentation, operational efficiency, and consistent customer experiences across every channel."
        nextProject={{
          title: "Lloyds Bank & Amazon Echo",
          link: "/project/lloyds",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Building systems for change, not certainty",
          description:
            "At KFC’s scale, the hardest problems aren’t technical — they’re organisational and operational. This project reinforced the importance of designing systems that embrace change: real-time data, distributed ownership, and business-driven configuration. Great backend engineering isn’t about control — it’s about enabling the organisation to move faster, safely.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Kfc };
